import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { Users, UserPlus, Clock } from 'lucide-react'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { TeamInviteForm } from '@/components/onboarding/TeamInviteForm'
import { Button } from '@personic/shared'

export default function InviteTeam() {
  const {
    teamInvites,
    addTeamInvite,
    removeTeamInvite,
    updateTeamInvite,
    setStep,
  } = useOnboardingStore()

  // Ensure we're on step 5
  useEffect(() => {
    setStep(5)
  }, [setStep])

  const handleAddFirstInvite = () => {
    if (teamInvites.length === 0) {
      addTeamInvite({ email: '', role: 'member' })
    }
  }

  const validInvites = teamInvites.filter((invite) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(invite.email)
  })

  return (
    <div>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-copper-100 flex items-center justify-center">
            <Users className="w-5 h-5 text-copper-600" />
          </div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-midnight-900">
            Invite your team
          </h1>
        </div>
        <p className="text-warm-600">
          Add team members to collaborate with you on Personic AI. You can always invite
          more people later.
        </p>
      </motion.div>

      {/* Skip Option */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6 p-4 bg-midnight-50 rounded-xl flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-midnight-500" />
          <p className="text-sm text-midnight-700">
            This step is <strong className="font-heading">optional</strong>. You can
            skip it and invite team members later.
          </p>
        </div>
      </motion.div>

      {/* Empty State */}
      {teamInvites.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center py-12 bg-warm-50/50 rounded-2xl border-2 border-dashed border-warm-200"
        >
          <div className="w-16 h-16 rounded-full bg-warm-100 flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-warm-400" />
          </div>
          <h3 className="text-lg font-heading font-semibold text-midnight-900 mb-2">
            No team members yet
          </h3>
          <p className="text-warm-500 mb-6 max-w-sm mx-auto">
            Invite colleagues to collaborate with you on Personic AI.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={handleAddFirstInvite}
            leftIcon={<UserPlus size={18} />}
          >
            Add Team Member
          </Button>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <TeamInviteForm
            invites={teamInvites}
            onAdd={addTeamInvite}
            onRemove={removeTeamInvite}
            onUpdate={updateTeamInvite}
            maxInvites={5}
          />
        </motion.div>
      )}

      {/* Summary */}
      {validInvites.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center"
        >
          <p className="text-sm text-warm-600">
            <span className="font-heading font-semibold text-copper-600">
              {validInvites.length} {validInvites.length === 1 ? 'invitation' : 'invitations'}
            </span>{' '}
            will be sent when your account is approved.
          </p>
        </motion.div>
      )}

      {/* Roles Explanation */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mt-8 p-4 bg-warm-50 rounded-xl"
      >
        <h4 className="text-sm font-heading font-semibold text-midnight-900 mb-2">
          About roles:
        </h4>
        <ul className="space-y-2 text-sm text-warm-600">
          <li className="flex items-start gap-2">
            <span className="font-heading font-medium text-midnight-700 w-16 flex-shrink-0">
              Admin
            </span>
            <span>
              Can manage team members, billing, API keys, and all settings.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-heading font-medium text-midnight-700 w-16 flex-shrink-0">
              Member
            </span>
            <span>
              Can use Personic AI products and view analytics, but cannot manage
              settings.
            </span>
          </li>
        </ul>
      </motion.div>
    </div>
  )
}
